import { Typography } from '@musinsa/mds';
import clsx from 'clsx';
import { Fragment, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import type { SeedingInfoRow } from '../types';

/**
 * 캠페인 정보 영역 compound — Container / Title / Card / Table.
 *
 * - **Container**: 자식들을 묶는 wrapper. `gap-y-16 px-16` 만 책임 — vertical padding 없음.
 *   각 영역(Title/Card/Table)이 자체 padding 을 자유롭게 가질 수 있음 (padding 겹침 제거).
 * - **Title**: 섹션 제목 (예: "신청 정보"). `title18pxMed` typography.
 * - **Card**: 회색 박스 안내 (title + description). 캠페인 정보 카드.
 * - **Table**: label/value 정보 행 리스트.
 *
 * 사용:
 * ```tsx
 * <CampaignInfo.Container>
 *   <CampaignInfo.Card title="..." description="..." />
 *   <CampaignInfo.Table rows={...} />
 * </CampaignInfo.Container>
 *
 * <CampaignInfo.Container>
 *   <CampaignInfo.Title>신청 정보</CampaignInfo.Title>
 *   <CampaignInfo.Table rows={...} labelWidth={100} />
 * </CampaignInfo.Container>
 * ```
 */

interface ContainerProps {
  children: ReactNode;
  /** 페이지 최상단 섹션(위에 divider 없음) — 상단 여백 24px. (그 외 섹션은 divider 와 합쳐 24) */
  firstSection?: boolean;
  className?: string;
}

// 기본 pt/pb 12 — divider(py-12) 와 합쳐 섹션 경계 24px. (SID-4329)
// 최상단(firstSection)은 위 divider 가 없어 24. 마지막 섹션의 하단 클리어런스(72)는 호출부가
// className(pb-[72px]!)로 지정 — twMerge 가 기본 pb-12 를 덮어쓴다. (단일-사용이던 lastSection prop 제거)
const Container = ({ children, firstSection, className }: ContainerProps) => (
  <section
    className={twMerge(
      'flex flex-col gap-y-[16px]! pt-[12px]! pb-[12px]! px-[16px]!',
      firstSection && 'pt-[24px]!',
      className,
    )}
  >
    {children}
  </section>
);

interface TitleProps {
  children: ReactNode;
  className?: string;
}

/**
 * 섹션 제목. typography `title18pxMed` (Figma 정합 — 페이지 H1 자리도 동일 typography).
 * default 로 h2 로 렌더. 다른 semantic tag 가 필요하면 Typography asChild 패턴을 호출부에서 직접 사용.
 */
const Title = ({ children, className }: TitleProps) => (
  <Typography variant="title18pxMed" colorScheme="black" asChild>
    <h2 className={clsx('', className)}>{children}</h2>
  </Typography>
);

interface CardProps {
  title?: string;
  description?: string;
  className?: string;
}

const Card = ({ title, description, className }: CardProps) => {
  if (!title && !description) return null;
  return (
    <div
      className={clsx(
        'bg-[#f5f5f5] rounded-[4px] px-[10px]! py-[8px]! min-h-[56px] flex gap-[8px] items-center',
        className,
      )}
    >
      <div className="flex flex-col gap-px flex-1 min-w-0">
        {title && (
          <Typography variant="body13pxSemi" colorScheme="black">
            {title}
          </Typography>
        )}
        {description && (
          <Typography size="12" weight="regular" colorScheme="gray-600">
            {description}
          </Typography>
        )}
      </div>
    </div>
  );
};

interface TableProps {
  rows: SeedingInfoRow[];
  /** label 컬럼 폭 (px). default = 80 (Card 옆 작은 폭) / Title 옆에선 100 권장 */
  labelWidth?: number;
  className?: string;
}

const Table = ({ rows, labelWidth = 80, className }: TableProps) => {
  if (rows.length === 0) return null;
  return (
    // 2col grid — 세로 gap 8px, 가로 gap 16px. (피드백 항목2) row head 타이포는 reg(굵기) 유지,
    // 색상은 label/value 모두 black. (SID-4150 연계 — Figma 1-24185 발송 예정일 행 black)
    <div
      className={clsx('grid items-center gap-x-[16px]! gap-y-[8px]!', className)}
      style={{ gridTemplateColumns: `${labelWidth}px 1fr` }}
    >
      {rows.map((row) => (
        <Fragment key={`${row.label}-${row.value}`}>
          <Typography variant="body13pxReg" colorScheme="black">
            {row.label}
          </Typography>
          <Typography variant="body13pxReg" colorScheme="black">
            {row.value}
          </Typography>
        </Fragment>
      ))}
    </div>
  );
};

export const CampaignInfo = {
  Container,
  Title,
  Card,
  Table,
};
